import { motion } from "framer-motion";
import { useEffect } from "react";
import { redirect, useLoaderData } from "react-router-dom";

import { get } from "@/api";
import { Image } from "@/atoms";
import {
  CURRENT_ORDER_QUERY_KEY,
  invalidateCurrentOrderDetails,
} from "@/modules/cart/useCartDetails";
import { CheckoutSuccessContainer } from "@/modules/checkout";
import { LocalStorageService } from "@/modules/storage";
import { waitObj } from "@/toolbox/Promise";

import { queryClient } from "../../Root";

async function loader() {
  let platform;
  try {
    platform = await queryClient.fetchQuery(["platform"], () =>
      get("/platform")
    );
  } catch (error) {
    console.error("Error fetching platform data:", error);
    throw redirect("/");
  }

  if (!platform?.platformSettings?.giftCardEnabled) {
    throw redirect("/");
  }

  return waitObj({
    platform,
  });
}
type LoaderData = Awaited<ReturnType<typeof loader>>;

export const giftCardSuccessLoader = loader;

const GiftCardSuccessPage = () => {
  const { platform } = useLoaderData() as LoaderData;

  useEffect(() => {
    queryClient.setQueryData(CURRENT_ORDER_QUERY_KEY, {
      shopDisplayOrderLineItems: [],
      estimatedTotal: 0,
      preFeeTotal: 0,
    });

    invalidateCurrentOrderDetails();
    queryClient.invalidateQueries({ queryKey: ["/orders/current/summary"] });
    queryClient.invalidateQueries({ queryKey: ["/orders/current/lineitems"] });
  }, []);

  const giftCardInfo = LocalStorageService.getItem("giftCardInfo");
  const giftCardInfoObject = giftCardInfo ? JSON.parse(giftCardInfo) : null;

  const buildPurchaseUrl = () => {
    const params = new URLSearchParams();

    if (giftCardInfoObject) {
      if (giftCardInfoObject.giftCardListingId) {
        params.set("giftCardListingId", giftCardInfoObject.giftCardListingId);
      }
      if (giftCardInfoObject.giftCardItemId) {
        params.set("giftCardItemId", giftCardInfoObject.giftCardItemId);
      }
      if (giftCardInfoObject.sharingPersonFirstName) {
        params.set(
          "sharingPersonFirstName",
          giftCardInfoObject.sharingPersonFirstName
        );
      }
      if (giftCardInfoObject.sharingPersonLastName) {
        params.set(
          "sharingPersonLastName",
          giftCardInfoObject.sharingPersonLastName
        );
      }
      if (giftCardInfoObject.sharingPersonPhone) {
        params.set("sharingPersonPhone", giftCardInfoObject.sharingPersonPhone);
      }
      if (giftCardInfoObject.sharingPersonEmail) {
        params.set("sharingPersonEmail", giftCardInfoObject.sharingPersonEmail);
      }
    }

    const queryString = params.toString();
    const finalUrl = queryString
      ? `/gift-cards/purchase?${queryString}`
      : "/gift-cards/purchase";

    return finalUrl;
  };

  return (
    <div className="GiftCardSuccessPage relative flex h-auto min-h-[calc(100vh-64px)] flex-col overflow-hidden lg:h-[calc(100vh-64px)] lg:flex-row">
      <motion.div
        className="top-0 flex items-center justify-center bg-brand-color/5 lg:sticky lg:h-screen lg:w-1/2 lg:flex-1"
        initial={{ opacity: 0, x: 0 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, ease: "easeInOut", delay: 0.4 }}
      >
        <div className="flex min-h-64 max-w-xl items-center justify-center">
          <Image
            src={platform.logoImageUrl ?? ""}
            alt="Platform logo"
            className="h-16 w-auto object-contain transition-all duration-300 sm:h-20 md:h-24 lg:h-32 xl:h-40 2xl:h-48"
          />
        </div>
      </motion.div>
      <motion.div
        className="flex flex-1 justify-center bg-gray-50 p-6 lg:h-full lg:overflow-y-auto"
        initial={{ opacity: 0, x: 0 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, ease: "easeInOut", delay: 0.2 }}
      >
        <div className="flex w-full flex-col justify-center py-4 sm:max-w-xl">
          <CheckoutSuccessContainer
            platform={platform}
            rewards={[]}
            primaryCta={{
              text: "Buy another gift card",
              link: buildPurchaseUrl(),
            }}
            errorCta={{ text: "Back to cart", link: "/gift-cards/cart" }}
          />
        </div>
      </motion.div>
    </div>
  );
};

export default GiftCardSuccessPage;
